import { useState, useEffect } from "react";
import { 
  Calendar, 
  Lock, 
  Unlock, 
  User, 
  MessageSquare, 
  Search, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  ShieldAlert,
  Users2,
  Users,
  Filter,
  BarChart3
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import Header from "../../components/Header";
import api from "../../connector/api";
import PremiumDatePicker from "../../components/PremiumDatePicker";
import RosterSelectorModal from "./components/EmployeeSelector";

export default function CheckAttendance() {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCode, setSelectedCode] = useState("");
  const [activeCommentRow, setActiveCommentRow] = useState(null); 
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [filterRequiredOnly, setFilterRequiredOnly] = useState(true); // Default to viewing only scheduled workers
  const [attendanceDate, setAttendanceDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [isLocked, setIsLocked] = useState(false);

  const savedRoster = localStorage.getItem("active_attendance_roster");
  const activeIds = savedRoster ? JSON.parse(savedRoster) : null;

  const fetchAttendance = async (date) => {
    setLoading(true);
    try {
      const response = await api.get("attendance/", {
        params: { date },
        withCredentials: true,
      });

      setAttendance(
        response.data.attendance.map((rec) => ({
          ...rec,
          code: rec.code || "",
          comment: rec.comment || "",
          is_locked: rec.is_locked || false,
          // If we have a saved roster, use it. Otherwise, default to true.
          requires_attendance: activeIds ? activeIds.includes(rec.id) : true,
        }))
      );
      setIsLocked(response.data.is_locked);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch attendance directory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance(attendanceDate);
  }, []);

  // 1. Updated saveRowData to accept the full record or target employee ID
  const saveRowData = async (record, code, currentComment) => {
    const actualEmployeeId = record.employee?.id || record.employee_id || record.id;

    if (!actualEmployeeId || (typeof actualEmployeeId === "string" && String(actualEmployeeId).startsWith("new_"))) {
      toast.error("Cannot save: Record does not have a valid Employee assigned.");
      return;
    }

    if (record.is_locked || isLocked) return;

    try {
      await api.post("attendance/save-row/", {
        employee_id: Number(actualEmployeeId), // ✅ MUST BE 'employee_id' to match Serializer
        code: code,
        comment: currentComment || "",
        date: attendanceDate,
      });
      toast.success(`Updated ${record.name || 'record'}`, { duration: 1500, id: `save-${actualEmployeeId}` });
    } catch (error) {
      console.error(error);
      toast.error(`Could not save records for ${record.name || 'employee'}`);
    }
  };

  // 2. Updated toggle handler
  const handleStatusToggle = (id, newCode) => {
    setAttendance((prev) =>
      prev.map((s) => {
        if (s.id === id) {
          const updated = { ...s, code: newCode };
          saveRowData(updated, newCode, s.comment); // Pass full updated record
          return updated;
        }
        return s;
      })
    );
  };

  // 3. Updated comment submission handler
  const handleCommentChangeSubmit = (id, commentText) => {
    setAttendance((prev) =>
      prev.map((s) => {
        if (s.id === id) {
          const updated = { ...s, comment: commentText };
          saveRowData(updated, s.code, commentText); // Pass full updated record
          return updated;
        }
        return s;
      })
    );
  };

  // 4. Updated quick macro fill
  const handleQuickMacroFill = (code) => {
    setSelectedCode(code);
    setAttendance((prev) =>
      prev.map((emp) => {
        if (emp.is_locked || isLocked || (filterRequiredOnly && !emp.requires_attendance)) return emp;
        
        const updated = { ...emp, code };
        saveRowData(updated, code, emp.comment); // Pass full updated record
        return updated;
      })
    );
    toast.success(`Batch updated visible records to: [${code}]`);
  };

  const toggleLock = async () => {
    try {
      const nextLockState = !isLocked;
      await api.post(
        "attendance/lock-toggle/",
        { date: attendanceDate, lock: nextLockState },
        { withCredentials: true }
      );
      setIsLocked(nextLockState);
      toast.success(`Roster ${nextLockState ? "locked" : "unlocked"} successfully`);
      setAttendance(prev => prev.map(rec => ({ ...rec, is_locked: nextLockState })));
    } catch (error) {
      console.error(error);
      toast.error("Authorization constraint modification execution failed");
    }
  };

  const handleSaveRosterInclusions = (selectedEmployeeIds) => {
    // Save selections to localStorage so it persists even if the user refreshes or changes days
    localStorage.setItem("active_attendance_roster", JSON.stringify(selectedEmployeeIds));

    setAttendance((prev) =>
      prev.map((emp) => ({
        ...emp,
        requires_attendance: selectedEmployeeIds.includes(emp.id),
      }))
    );
    toast.success("Roster layout saved successfully.");
  };

  // Multi-tier structural processing filter chain
  const filteredAttendance = attendance.filter(emp => {
    const matchesSearch = (emp.name || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRequirement = filterRequiredOnly ? emp.requires_attendance === true : true;
    return matchesSearch && matchesRequirement;
  });

  return (
    <div className="min-h-screen font-dynamic-title bg-slate-50 text-slate-800 flex flex-col pb-12 antialiased">
      <Toaster />
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-xs">
        <Header />
      </div>

      <main className="flex-1 max-w-md w-full mx-auto px-4 pt-4 space-y-4 flex flex-col">
        
        {/* Control Dashboard Panel */}
        <div className="bg-white rounded-xl border border-slate-200/80 p-4 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base font-black tracking-tight text-slate-900">Worship Attendance</h1>
              <p className="text-[11px] text-slate-400 font-medium">Verify daily presence states</p>
            </div>
            
            {/* Action Button Group Wrapper */}
            <div className="flex items-center gap-1.5">
              {/* Navigation Button to Reports Page */}
              <button
                type="button"
                onClick={() => window.location.href = "/attendance/reports"} // Change this path to match your router path setup
                className="p-2.5 rounded-xl transition-all border bg-slate-50 border-slate-200 text-slate-600 hover:text-[#002855] hover:bg-slate-100"
                title="View Attendance Insights"
              >
                <BarChart3 className="w-4 h-4 stroke-[2.5]" />
              </button>

              {/* Existing Lock/Unlock Button */}
              <button
                type="button"
                onClick={toggleLock}
                className={`p-2.5 rounded-xl transition-all border ${
                  isLocked 
                    ? "bg-rose-50 border-rose-200 text-rose-600" 
                    : "bg-blue-50/60 border-blue-100 text-[#002855]"
                }`}
              >
                {isLocked ? <Lock className="w-4 h-4 stroke-[2.5]" /> : <Unlock className="w-4 h-4 stroke-[2.5]" />}
              </button>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Configuration Inputs Toolbar Block */}
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <PremiumDatePicker 
                attendanceDate={attendanceDate} 
                setAttendanceDate={setAttendanceDate} 
                fetchAttendance={fetchAttendance} 
              />
                
              {/* Roster Selection Trigger Button */}
              <button
                type="button"
                onClick={() => setShowRosterModal(true)}
                className="h-9 px-3 bg-white border border-slate-200 hover:bg-slate-50 text-[#002855] rounded-xl font-bold text-[11px] transition flex items-center gap-1.5 shadow-2xs shrink-0"
              >
                <Users size={13} />
                <span>Set Active Staff</span>
              </button>
            </div>

            {/* Segmented Layout View Switch Filter */}
            <div className="bg-slate-100 p-1 rounded-xl grid grid-cols-2 gap-1 w-full border border-slate-200/30">
              <button
                type="button"
                onClick={() => setFilterRequiredOnly(true)}
                className={`py-1.5 text-[10px] font-bold rounded-lg transition dynamic-center gap-1.5 flex justify-center items-center ${
                  filterRequiredOnly 
                    ? "bg-white text-[#002855] shadow-2xs border border-slate-200/40" 
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <Filter size={11} className={filterRequiredOnly ? "text-[#002855]" : ""} />
                <span>Required Only ({attendance.filter(e => e.requires_attendance).length})</span>
              </button>
              <button
                type="button"
                onClick={() => setFilterRequiredOnly(false)}
                className={`py-1.5 text-[10px] font-bold rounded-lg transition dynamic-center gap-1.5 flex justify-center items-center ${
                  !filterRequiredOnly 
                    ? "bg-white text-[#002855] shadow-2xs border border-slate-200/40" 
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <Users2 size={11} className={!filterRequiredOnly ? "text-[#002855]" : ""} />
                <span>All Staff ({attendance.length})</span>
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Bulk Fill:</span>
            <div className="flex gap-1.5">
              {[
                { label: "P", color: "hover:bg-emerald-500 hover:text-white" },
                { label: "A", color: "hover:bg-rose-500 hover:text-white" },
                { label: "L", color: "hover:bg-amber-500 hover:text-white" }
              ].map((c) => (
                <button
                  key={c.label}
                  type="button"
                  onClick={() => handleQuickMacroFill(c.label)}
                  className={`w-8 h-8 rounded-lg text-xs font-extrabold transition border border-slate-200 bg-slate-50 text-slate-600 shadow-2xs ${c.color}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Finder Input */}
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search filtered roster maps..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white border border-slate-200 focus:border-[#002855] focus:ring-4 focus:ring-[#002855]/5 rounded-xl pl-10 pr-4 py-2.5 text-xs outline-none shadow-xs transition"
          />
        </div>

        {/* Card Stream */}
        <div className="flex-1 space-y-2.5">
          {loading ? (
            <div className="py-12 text-center text-xs font-medium text-slate-400 animate-pulse">
              Pulling dynamic employee logs...
            </div>
          ) : filteredAttendance.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200/80 py-12 px-4 text-center shadow-xs">
              <ShieldAlert className="w-5 h-5 text-slate-400 mx-auto stroke-[1.5]" />
              <p className="text-xs font-bold text-slate-700 mt-2">No Records Evaluated</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Adjust selection view filters or query parameters</p>
            </div>
          ) : (
            filteredAttendance.map((rec) => {
              const hasComment = rec.comment && rec.comment.trim().length > 0;
              return (
                <div 
                  key={rec.id} 
                  className={`bg-white rounded-2xl border transition-all p-3.5 shadow-xs flex flex-col gap-3 relative overflow-hidden ${
                    isLocked || rec.is_locked ? "opacity-75 bg-slate-50/50" : ""
                  }`}
                >
                  {/* Highlight indicator if looking at 'All Staff' and some don't require tracking */}
                  {!rec.requires_attendance && (
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-slate-200" title="Optional Track Status" />
                  )}

                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-slate-100 border border-slate-200/60 flex items-center justify-center text-slate-700 font-black text-xs shrink-0">
                        {rec.name?.charAt(0) || <User className="w-3.5 h-3.5 text-slate-400" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <h3 className="text-xs font-black text-slate-900 tracking-tight truncate">{rec.name}</h3>
                          {!rec.requires_attendance && (
                            <span className="text-[8px] bg-slate-100 px-1.5 py-0.5 rounded-md font-bold text-slate-400 uppercase tracking-wide shrink-0">
                              Exempt
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">EMP #{rec.position?.id_number}</p>
                      </div>
                    </div>

                    <button
                      type="button"
                      disabled={isLocked || rec.is_locked}
                      onClick={() => setActiveCommentRow(rec)}
                      className={`p-2 rounded-xl border transition relative shrink-0 ${
                        hasComment 
                          ? "bg-amber-50 border-amber-200 text-amber-600" 
                          : "bg-slate-50 border-slate-200 text-slate-400 hover:text-slate-600"
                      }`}
                    >
                      <MessageSquare className="w-3.5 h-3.5 stroke-[2]" />
                      {hasComment && (
                        <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-amber-500" />
                      )}
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5 mt-0.5">
                    {[
                      { code: "P", label: "Present", activeStyle: "bg-emerald-600 border-emerald-600 text-white shadow-xs", icon: CheckCircle2 },
                      { code: "A", label: "Absent", activeStyle: "bg-rose-600 border-rose-600 text-white shadow-xs", icon: XCircle },
                      { code: "L", label: "Late", activeStyle: "bg-amber-500 border-amber-500 text-white shadow-xs", icon: Clock }
                    ].map((statusDef) => {
                      const isSelected = rec.code === statusDef.code;
                      const Icon = statusDef.icon;
                      return (
                        <button
                          key={statusDef.code}
                          type="button"
                          disabled={isLocked || rec.is_locked}
                          onClick={() => handleStatusToggle(rec.id, statusDef.code)}
                          className={`h-9 rounded-xl border text-[11px] font-black transition-all flex items-center justify-center gap-1.5 ${
                            isSelected 
                              ? statusDef.activeStyle 
                              : "bg-slate-50/60 border-slate-200 text-slate-500 active:bg-slate-100"
                          }`}
                        >
                          <Icon className={`w-3.5 h-3.5 ${isSelected ? "stroke-[3]" : "stroke-[2]"}`} />
                          <span>{statusDef.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  
                  {hasComment && (
                    <div className="bg-amber-50/50 border border-amber-100/60 rounded-xl px-2.5 py-1.5 text-[10px] text-amber-800 font-medium leading-relaxed italic truncate">
                      “{rec.comment}”
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </main>

      {/* Mobile Drawer */}
      {activeCommentRow && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 backdrop-blur-xs p-4 animate-fade-in">
          <div className="absolute inset-0" onClick={() => setActiveCommentRow(null)} />
          
          <div className="bg-white w-full max-w-md rounded-2xl shadow-xl border border-slate-200 z-10 flex flex-col p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-xs font-black text-slate-900">Add Roster Annotation Note</h4>
                <p className="text-[10px] text-slate-400 mt-0.5">Context tracking logs for {activeCommentRow.name}</p>
              </div>
              <button 
                type="button" 
                onClick={() => setActiveCommentRow(null)}
                className="text-slate-400 text-xs font-extrabold hover:text-slate-600 bg-slate-100 px-2 py-1 rounded-lg"
              >
                Close
              </button>
            </div>

            <textarea
              rows={3}
              value={activeCommentRow.comment}
              placeholder="Provide context regarding delays or sudden unexcused leaves..."
              onChange={(e) => {
                const text = e.target.value;
                setAttendance(prev => prev.map(s => s.id === activeCommentRow.id ? { ...s, comment: text } : s));
                setActiveCommentRow(prev => ({ ...prev, comment: text }));
              }}
              className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-amber-500 rounded-xl p-3 text-xs outline-none transition resize-none shadow-2xs"
            />

            <button
              type="button"
              onClick={() => {
                handleCommentChangeSubmit(activeCommentRow.id, activeCommentRow.comment);
                setActiveCommentRow(null);
              }}
              className="h-10 bg-[#002855] bg-opacity-80 hover:bg-opacity-95 text-white text-xs font-bold rounded-xl transition w-full shadow-md hover:bg-opacity-90 active:scale-[0.99]"
            >
              Commit Annotation Record
            </button>
          </div>
        </div>
      )}

      <RosterSelectorModal
        show={showRosterModal}
        setShow={setShowRosterModal}
        allEmployees={attendance}
        currentRequiredIds={attendance.filter(e => e.requires_attendance).map(e => e.id)}
        onSave={handleSaveRosterInclusions}
      />
    </div>
  );
}